import { fromPromise, setup } from 'xstate';
import { PaymentMethod } from '@ads-bread/shared/bread/codecs';
import { ActorErrorParams } from '../../../../lib/types';
import {
  PaymentMachineEvents,
  SendCreatePaymentMethodParams,
  SendFetchPaymentMethodParams,
  SendFetchPaymentMethodsParams,
} from './types/events';
import {
  CreatePaymentMethodResult,
  EvaluateErrorResponseResult,
  FetchPaymentMethodResult,
  FetchPaymentMethodsResult,
} from './types/actors';
import {
  getCreatePaymentMethodInput,
  getFetchPaymentMethodInput,
  getFetchPaymentMethodsInput,
} from './inputs';
import {
  evaluateErrorResponseParams,
  getAssignSelectedPaymentMethodParams,
  getCreatePaymentMethodParams,
  getEvaluateErrorResponseEntryParams,
  getFetchPaymentMethodParams,
  getFetchPaymentMethodsParams,
  getServiceErrorParams,
} from './params';
import {
  assignPaymentMethodResult,
  assignPaymentMethodsResult,
  assignSelectedPaymentMethod,
} from './assigns';

export interface PaymentsContext {
  paymentMethods: PaymentMethod[];
  selectedPaymentMethodID: string | null;
}

export const paymentsMachine = setup({
  types: {
    input: {} as Partial<PaymentsContext>,
    context: {} as PaymentsContext,
    events: {} as PaymentMachineEvents,
  },
  guards: {
    isPaymentsErrorResponse: (
      _,
      __:
        | FetchPaymentMethodResult
        | FetchPaymentMethodsResult
        | CreatePaymentMethodResult
    ): boolean => {
      throw new Error('Not implemented');
    },
    isInvalidPaymentMethodError: (
      _,
      __: EvaluateErrorResponseResult
    ): boolean => {
      throw new Error('Not implemented');
    },
    isWriteCardAccountError: (_, __: EvaluateErrorResponseResult): boolean => {
      throw new Error('Not implemented');
    },
    isGetCardConnectionError: (_, __: EvaluateErrorResponseResult): boolean => {
      throw new Error('Not implemented');
    },
    isTransactionError: (_, __: EvaluateErrorResponseResult): boolean => {
      throw new Error('Not implemented');
    },
    isGetPaymentMethodError: (_, __: EvaluateErrorResponseResult): boolean => {
      throw new Error('Not implemented');
    },
    isAuthorizePaymentError: (_, __: EvaluateErrorResponseResult): boolean => {
      throw new Error('Not implemented');
    },
    isCardTypeMismatchError: (_, __: EvaluateErrorResponseResult): boolean => {
      throw new Error('Not implemented');
    },
  },
  actions: {
    assignPaymentMethodResult,
    assignSelectedPaymentMethod,
    assignPaymentMethodsResult,
    handleFetchPaymentMethodDone: (_, __: FetchPaymentMethodResult) => {
      throw new Error('Not implemented');
    },
    handleFetchPaymentMethodsDone: (_, __: FetchPaymentMethodsResult) => {
      throw new Error('Not implemented');
    },
    handleCreatePaymentMethodDone: (_, __: CreatePaymentMethodResult) => {
      throw new Error('Not implemented');
    },
    handleServiceError: (_, __: ActorErrorParams): void => {
      throw new Error('Not implemented');
    },
    logError: (_, __: ActorErrorParams): void => {
      throw new Error('Not implemented');
    },
    logEvaluatedErrorResponse: (_, __: unknown): void => {
      throw new Error('Not implemented');
    },
  },
  actors: {
    fetchPaymentMethod: fromPromise<
      FetchPaymentMethodResult,
      SendFetchPaymentMethodParams
    >(() => {
      throw new Error('Not implemented');
    }),
    fetchPaymentMethods: fromPromise<
      FetchPaymentMethodsResult,
      SendFetchPaymentMethodsParams
    >(() => {
      throw new Error('Not implemented');
    }),
    createPaymentMethod: fromPromise<
      CreatePaymentMethodResult,
      SendCreatePaymentMethodParams
    >(() => {
      throw new Error('Not implemented');
    }),
    evaluateErrorResponse: fromPromise<EvaluateErrorResponseResult, unknown>(
      () => {
        throw new Error('Not implemented');
      }
    ),
  },
}).createMachine({
  id: 'payments',
  context: ({ input }) => {
    return {
      paymentMethods: input?.paymentMethods || [],
      selectedPaymentMethodID: input?.selectedPaymentMethodID || null,
    };
  },
  initial: 'idle',
  on: {
    SEND_FETCH_PAYMENT_METHOD: {
      target: '.fetchingPaymentMethod',
    },
    SEND_FETCH_PAYMENT_METHODS: {
      target: '.fetchingPaymentMethods',
    },
    SEND_CREATE_PAYMENT_METHOD: {
      target: '.creatingPaymentMethod',
    },
    SEND_ASSIGN_SELECTED_PAYMENT_METHOD: {
      actions: {
        type: 'assignSelectedPaymentMethod',
        params: getAssignSelectedPaymentMethodParams,
      },
    },
  },
  states: {
    idle: {},
    fetchingPaymentMethod: {
      invoke: {
        src: 'fetchPaymentMethod',
        id: 'fetchPaymentMethod',
        input: getFetchPaymentMethodInput,
        onDone: [
          {
            guard: {
              type: 'isPaymentsErrorResponse',
              params: getFetchPaymentMethodParams,
            },
            actions: [
              {
                type: 'handleFetchPaymentMethodDone',
                params: getFetchPaymentMethodParams,
              },
            ],
            target: 'evaluatingErrorResponse',
          },
          {
            actions: [
              {
                type: 'assignPaymentMethodResult',
                params: getFetchPaymentMethodParams,
              },
              {
                type: 'handleFetchPaymentMethodDone',
                params: getFetchPaymentMethodParams,
              },
            ],
            target: 'idle',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'handleServiceError',
                params: getServiceErrorParams,
              },
              {
                type: 'logError',
                params: getServiceErrorParams,
              },
            ],
            target: 'recoverableError.default',
          },
        ],
      },
    },
    fetchingPaymentMethods: {
      invoke: {
        src: 'fetchPaymentMethods',
        id: 'fetchPaymentMethods',
        input: getFetchPaymentMethodsInput,
        onDone: [
          {
            guard: {
              type: 'isPaymentsErrorResponse',
              params: getFetchPaymentMethodsParams,
            },
            actions: [
              {
                type: 'handleFetchPaymentMethodsDone',
                params: getFetchPaymentMethodsParams,
              },
            ],
            target: 'evaluatingErrorResponse',
          },
          {
            actions: [
              {
                type: 'assignPaymentMethodsResult',
                params: getFetchPaymentMethodsParams,
              },
              {
                type: 'handleFetchPaymentMethodsDone',
                params: getFetchPaymentMethodsParams,
              },
            ],
            target: 'idle',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'handleServiceError',
                params: getServiceErrorParams,
              },
              {
                type: 'logError',
                params: getServiceErrorParams,
              },
            ],
            target: 'recoverableError.default',
          },
        ],
      },
    },
    creatingPaymentMethod: {
      invoke: {
        src: 'createPaymentMethod',
        id: 'createPaymentMethod',
        input: getCreatePaymentMethodInput,
        onDone: [
          {
            guard: {
              type: 'isPaymentsErrorResponse',
              params: getCreatePaymentMethodParams,
            },
            actions: [
              {
                type: 'handleCreatePaymentMethodDone',
                params: getCreatePaymentMethodParams,
              },
            ],
            target: 'evaluatingErrorResponse',
          },
          {
            actions: [
              {
                type: 'assignPaymentMethodResult',
                params: getCreatePaymentMethodParams,
              },
              {
                type: 'handleCreatePaymentMethodDone',
                params: getCreatePaymentMethodParams,
              },
            ],
            target: 'idle',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'handleServiceError',
                params: getServiceErrorParams,
              },
              {
                type: 'logError',
                params: getServiceErrorParams,
              },
            ],
            target: 'recoverableError.default',
          },
        ],
      },
    },
    evaluatingErrorResponse: {
      entry: [
        {
          type: 'logEvaluatedErrorResponse',
          params: getEvaluateErrorResponseEntryParams,
        },
      ],
      invoke: {
        src: 'evaluateErrorResponse',
        id: 'evaluateErrorResponse',
        input: getEvaluateErrorResponseEntryParams,
        onDone: [
          {
            guard: {
              type: 'isInvalidPaymentMethodError',
              params: evaluateErrorResponseParams,
            },
            target: 'recoverableError.invalidPaymentMethodError',
          },
          {
            guard: {
              type: 'isWriteCardAccountError',
              params: evaluateErrorResponseParams,
            },
            target: 'recoverableError.writeCardAccountError',
          },
          {
            guard: {
              type: 'isGetCardConnectionError',
              params: evaluateErrorResponseParams,
            },
            target: 'recoverableError.getCardConnectionError',
          },
          {
            guard: {
              type: 'isTransactionError',
              params: evaluateErrorResponseParams,
            },
            target: 'recoverableError.transactionError',
          },
          {
            guard: {
              type: 'isGetPaymentMethodError',
              params: evaluateErrorResponseParams,
            },
            target: 'recoverableError.getPaymentMethodError',
          },
          {
            guard: {
              type: 'isAuthorizePaymentError',
              params: evaluateErrorResponseParams,
            },
            target: 'recoverableError.authorizePaymentError',
          },
          {
            guard: {
              type: 'isCardTypeMismatchError',
              params: evaluateErrorResponseParams,
            },
            target: 'recoverableError.cardTypeMismatchError',
          },
          {
            target: 'recoverableError.default',
          },
        ],
        onError: [
          {
            target: 'recoverableError.default',
          },
        ],
      },
    },
    recoverableError: {
      initial: 'default',
      states: {
        default: {},
        invalidPaymentMethodError: {},
        writeCardAccountError: {},
        getCardConnectionError: {},
        transactionError: {},
        getPaymentMethodError: {},
        authorizePaymentError: {},
        cardTypeMismatchError: {},
      },
    },
  },
});
